import { useEffect, useState } from 'react';

import { useMapData } from 'hooks/map';
import { useMapContextMenu } from 'hooks/map/useMapContentMenu';
import { useMarkerContextMenu } from 'hooks/map/useMarkerContextMenu';
import { MarkerData, StateData, StateGroupData } from 'types/map';

import { MapConfig } from '.';
import { ConfigSaver } from './controls/ConfigSaver';
import { MapComponent } from './controls/MapComponent';
import { SelectedCounter } from './controls/SelectedCounter';
import { TemplateNameEditor } from './controls/TemplateNameEditor';
import * as Styled from './index.styled';
import { MarkerCtxMenu } from './MarkerCtxMenu';
import { StateCtxMenu } from './StateCtxMenu';

type MapEditorProps = {
    open?: boolean;
    onClose?: () => void;
    defaultConfig?: MapConfig & { name?: string };
    onSave?: (config: MapConfig & { name?: string }) => void;
};

export const MapEditor = ({
    onClose = () => undefined,
    open = false,
    defaultConfig,
    onSave = () => undefined,
}: MapEditorProps): JSX.Element => {
    const [states, setStates] = useState<StateData[]>([]);
    const [groups, setGroups] = useState<StateGroupData[]>([]);
    const [hidden, setHidden] = useState<number[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [markers, setMarkers] = useState<MarkerData[]>([]);

    const [templateName, setTemplateName] = useState<string | undefined>();

    useEffect(() => {
        setStates(defaultConfig?.states || []);
        setGroups(defaultConfig?.groups || []);
        setHidden(defaultConfig?.hidden || []);
        setMarkers(defaultConfig?.markers || []);
        setTemplateName(defaultConfig?.name);
        setSelected([]);
    }, [defaultConfig, open]);

    const {
        ctxMenuAnchorPositions,
        currentGeoID,
        handleCtxMenu,
        handleCtxMenuClose,
    } = useMapContextMenu();

    const {
        ctxMenuAnchorPositions: markerMenu,
        currentMarkerID,
        handleCtxMenu: handleMarkerMenu,
        handleCtxMenuClose: closeMarkerMenu,
    } = useMarkerContextMenu();

    const { data: mapData } = useMapData({
        states,
        groups,
    });

    const toggleSelected = (id: number): void => {
        if (selected.includes(id)) {
            setSelected(selected.filter((el) => el !== id));
        } else {
            setSelected([...selected, id]);
        }
    };

    return (
        <>
            <Styled.Modal open={open}>
                <Styled.ModalContent>
                    <MapComponent
                        data={mapData}
                        onCtxMenu={handleCtxMenu}
                        onMarkerClick={handleMarkerMenu}
                        onClick={(e, geo) => {
                            if (selected.length) {
                                toggleSelected(geo.id);
                            }
                        }}
                        hidden={hidden}
                        selected={selected}
                        markers={markers}
                    />
                    <ConfigSaver
                        onCancel={() => {
                            onClose();
                        }}
                        onSave={() => {
                            onSave({
                                groups,
                                hidden,
                                states,
                                markers,
                                name: templateName,
                            });
                        }}
                    />
                    <SelectedCounter
                        selected={selected}
                        data={mapData}
                        onCancel={() => setSelected([])}
                        onToggle={toggleSelected}
                    />
                    {templateName ? (
                        <TemplateNameEditor
                            onChange={setTemplateName}
                            value={templateName}
                        />
                    ) : undefined}
                </Styled.ModalContent>
            </Styled.Modal>
            <StateCtxMenu
                geometries={mapData.objects.russia.geometries}
                menuAnchor={ctxMenuAnchorPositions}
                currentGeoID={currentGeoID}
                states={states}
                groups={groups}
                hidden={hidden}
                markers={markers}
                selected={selected}
                onClose={handleCtxMenuClose}
                setStates={setStates}
                setGroups={setGroups}
                setHidden={setHidden}
                setMarkers={setMarkers}
                setSelected={setSelected}
            />
            <MarkerCtxMenu
                markerID={currentMarkerID}
                markers={markers}
                menuAnchor={markerMenu}
                onClose={closeMarkerMenu}
                setMarkers={setMarkers}
            />
        </>
    );
};
